// Outside redux-persist: every tab rewrites `persist:root` wholesale, so a second tab restored the old toggles.

export type BooleanPreferences = Record<string, boolean>

const PERSIST_ROOT_KEY = 'persist:root'

const readRaw = (key: string): string | null => {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const parse = (raw: string | null): unknown => {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

// Storage is user-writable and an older release's shape is missing every key added since.
const sanitize = <T extends BooleanPreferences>(stored: unknown, defaults: T): T => {
  const next: BooleanPreferences = { ...defaults }
  if (stored && typeof stored === 'object') {
    const raw = stored as Record<string, unknown>
    Object.keys(defaults).forEach(key => {
      if (typeof raw[key] === 'boolean') next[key] = raw[key] as boolean
    })
  }
  return next as T
}

// Carries the pre-move value over; without it every user's customization resets once on deploy.
const readLegacy = (persistRootKey: string): unknown => {
  const root = parse(readRaw(PERSIST_ROOT_KEY))
  if (!root || typeof root !== 'object') return null
  return parse((root as Record<string, string>)[persistRootKey] ?? null)
}

export const readPreferences = <T extends BooleanPreferences>(storageKey: string, persistRootKey: string, defaults: T): T => {
  const stored = parse(readRaw(storageKey))
  return sanitize(stored ?? readLegacy(persistRootKey), defaults)
}

export const writePreferences = (storageKey: string, preferences: BooleanPreferences): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(preferences))
  } catch {
    /* swallow — Safari private-mode and similar */
  }
}
